(function () {
  const STATE = {
    IDLE: "idle",
    CHOOSING: "choosing",
    UPLOADING: "uploading",
    PROCESSING: "processing",
    READY: "ready",
    APPLIED: "applied",
    ERROR: "error",
  };
  // After this long in a waiting state (upload, processing, apply) the status line switches to
  // the patience hint — see syncSlowHint.
  const SLOW_HINT_DELAY_MS = 5000;
  const DEFAULT_PLAYMAT_MOCKUP_URL =
    "https://cdn.shopify.com/s/files/1/0733/1099/4705/files/Playmat_Mockup-Konfigurator-Clear.png?v=1729084249";
  // Mirrors the backend's MIN_CROP_EDGE_PIXELS (image-processing.js). The server stays
  // authoritative — if the values ever drift, its (retryable, localized) IMAGE_TOO_SMALL
  // error still catches what slips through this client-side pre-check.
  const MIN_CROP_EDGE_PIXELS = 100;

  class HypematCustomUploader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.state = STATE.IDLE;
      this.file = null;
      this.declaredType = "";
      this.generation = 0;
      this.session = null;
      this.completeTimeout = null;
      this.objectUrl = null;
      this.objectUrlIsLocal = false;
      this.previewDataUrl = "";
      this.imageMetrics = null;
      // Pixel size of the ORIGINAL file as decoded by the browser — the print-file proof shown in
      // the chip. Distinct from imageMetrics, which follows the working bitmap (rotation bakes are
      // capped at 4096, server rasters for PDF/HEIC likewise). Null when we cannot know it.
      this.sourceDimensions = null;
      // Revocable object URL for the collapsed-chip thumbnail (replaces a multi-MB base64 data URL).
      this.croppedBlobUrl = null;
      // Touch-crop gesture state: live pointers for pinch, last clean tap for double-tap detection,
      // and whether the current gesture ever became multi-touch (so a pinch is never read as a tap).
      this.activePointers = new Map();
      this.lastTap = null;
      this.gestureMultiTouch = false;
      // Per-format crop aspect ratio ({label: "W:H", value: W/H}), parsed once from the
      // data-crop-aspect attribute the snippet derives from the block's upload_format.
      this.cropAspect = this.parseCropAspect();
      // Where the artwork band sits inside the square mockup (fractions, from
      // data-artwork-frame via hypemat-format-frame). Null = classic centred full-width band.
      this.artworkFrame = this.parseArtworkFrame();
      this.crop = {
        x: 0.5,
        y: 0.5,
        scale: 1,
        // User rotation in clockwise 90° steps (0/90/180/270), applied on top of the
        // EXIF-upright image — the browser shows EXIF-upright, the backend normalizes
        // via sharp().rotate() first, so both sides share the same base orientation.
        rotation: 0,
        aspectRatio: this.cropAspect.label,
      };
      // Blob URL of the client-side rotated working bitmap (display only — production is
      // re-rendered server-side from the original with the same rotation).
      this.rotatedUrl = null;
      this.rotationToken = 0;
      this.rotationSourceImage = null;
      this.rotationSourceUrl = null;
      this.rotationBakePending = false;
      // Zone overlay in the crop frame is on by default whenever the variant has one; the
      // customer can hide it via the toggle for an unobstructed look at the motif.
      this.zonesVisible = true;
      this.mockupMap = this.parseMockupMap();
      this.form = document.getElementById(this.dataset.formId);
      this.fileInput = this.querySelector("[data-hypemat-file-input]");
      this.openButton = this.querySelector("[data-hypemat-open]");
      this.inline = this.querySelector("[data-hypemat-inline]");
      this.cancelButton = this.querySelector("[data-hypemat-cancel]");
      this.changeButton = this.querySelector("[data-hypemat-change]");
      this.dropzone = this.querySelector("[data-hypemat-dropzone]");
      this.statusElement = this.querySelector("[data-hypemat-status]");
      this.errorElement = this.querySelector("[data-hypemat-error]");
      this.retryButton = this.querySelector("[data-hypemat-retry]");
      this.introError = this.querySelector("[data-hypemat-intro-error]");
      this.introHint = this.querySelector("[data-hypemat-intro-hint]");
      this.resetButton = this.querySelector("[data-hypemat-preview-reset]");
      this.cropElement = this.querySelector("[data-hypemat-crop]");
      this.cropFrame = this.querySelector("[data-hypemat-crop-frame]");
      this.cropImage = this.querySelector("[data-hypemat-crop-image]");
      // Safety net for the gallery overlay: if it was created before the crop image had decoded
      // (no baked crop available yet), bake it as soon as the image loads — independent of the
      // per-flow onload handlers, which get reassigned along the way.
      this.previewBakePending = false;
      if (this.cropImage) {
        this.cropImage.addEventListener("load", () => {
          if (this.previewBakePending) this.updateProductPreviewArtwork();
        });
      }
      this.cropScaleInput = this.querySelector("[data-hypemat-crop-scale]");
      this.zoomInButton = this.querySelector("[data-hypemat-zoom-in]");
      this.zoomOutButton = this.querySelector("[data-hypemat-zoom-out]");
      this.rotateButton = this.querySelector("[data-hypemat-rotate]");
      this.cropZones = this.querySelector("[data-hypemat-crop-zones]");
      this.zonesToggle = this.querySelector("[data-hypemat-zones-toggle]");
      this.cropControls = this.querySelector("[data-hypemat-crop-controls]");
      this.previewEmpty = this.querySelector("[data-hypemat-preview-empty]");
      this.fileMeta = this.querySelector("[data-hypemat-file-meta]");
      this.fileName = this.querySelector("[data-hypemat-file-name]");
      this.fileSize = this.querySelector("[data-hypemat-file-size]");
      this.confirmButton = this.querySelector("[data-hypemat-confirm]");
      this.chip = this.querySelector("[data-hypemat-chip]");
      this.chipThumb = this.querySelector("[data-hypemat-chip-thumb]");
      this.chipIcon = this.querySelector("[data-hypemat-chip-icon]");
      this.chipName = this.querySelector("[data-hypemat-chip-name]");
      this.chipMeta = this.querySelector("[data-hypemat-chip-meta]");
      this.chipMetaText = this.querySelector("[data-hypemat-chip-meta-text]");
      this.chipEdit = this.querySelector("[data-hypemat-edit]");
      this.sticky = this.querySelector("[data-hypemat-sticky]");
      this.stickyAction = this.querySelector("[data-hypemat-sticky-action]");
      this.hostStatus = this.querySelector("[data-hypemat-host-status]");
      this.hostError = this.querySelector("[data-hypemat-host-error]");

      this.onFileChange = this.handleFileChange.bind(this);
      this.onOpenInline = this.openInline.bind(this);
      this.onCancel = this.cancelToIdle.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onEdit = this.handleEdit.bind(this);
      this.onChooseFile = this.chooseFile.bind(this);
      this.onDropzoneDragEnter = this.handleDropzoneDragEnter.bind(this);
      this.onDropzoneDragLeave = this.handleDropzoneDragLeave.bind(this);
      this.onDropzoneDrop = this.handleDropzoneDrop.bind(this);
      this.onReset = this.reset.bind(this);
      this.onConfirm = this.handleConfirm.bind(this);
      this.onRetry = this.handleRetry.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onVariantAdded = this.handleVariantAdded.bind(this);
      this.onVariantChanged = this.handleVariantChanged.bind(this);
      this.onProductRerender = this.handleProductRerender.bind(this);
      this.onCropScaleInput = this.handleCropScaleInput.bind(this);
      this.onZoomIn = () => this.nudgeScale(0.1);
      this.onZoomOut = () => this.nudgeScale(-0.1);
      this.onRotate = this.rotateClockwise.bind(this);
      this.onZonesToggle = this.handleZonesToggle.bind(this);
      this.onCropKeydown = this.handleCropKeydown.bind(this);
      this.onCropPointerDown = this.handleCropPointerDown.bind(this);
      this.onCropPointerMove = this.handleCropPointerMove.bind(this);
      this.onCropPointerUp = this.handleCropPointerUp.bind(this);
      this.onCropLostCapture = this.handleCropLostCapture.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onSlideChange = this.handleSlideChange.bind(this);
      this.onStickyAction = this.handleStickyAction.bind(this);

      this.fileInput?.addEventListener("change", this.onFileChange);
      this.openButton?.addEventListener("click", this.onOpenInline);
      this.cancelButton?.addEventListener("click", this.onCancel);
      this.changeButton?.addEventListener("click", this.onChange);
      this.chipEdit?.addEventListener("click", this.onEdit);
      this.dropzone?.addEventListener("click", this.onChooseFile);
      this.dropzone?.addEventListener("dragenter", this.onDropzoneDragEnter);
      this.dropzone?.addEventListener("dragover", this.onDropzoneDragEnter);
      this.dropzone?.addEventListener("dragleave", this.onDropzoneDragLeave);
      this.dropzone?.addEventListener("drop", this.onDropzoneDrop);
      this.resetButton?.addEventListener("click", this.onReset);
      this.confirmButton?.addEventListener("click", this.onConfirm);
      this.retryButton?.addEventListener("click", this.onRetry);
      this.stickyAction?.addEventListener("click", this.onStickyAction);
      this.cropScaleInput?.addEventListener("input", this.onCropScaleInput);
      this.zoomInButton?.addEventListener("click", this.onZoomIn);
      this.zoomOutButton?.addEventListener("click", this.onZoomOut);
      this.rotateButton?.addEventListener("click", this.onRotate);
      this.zonesToggle?.addEventListener("click", this.onZonesToggle);
      this.cropFrame?.addEventListener("keydown", this.onCropKeydown);
      this.cropFrame?.addEventListener("pointerdown", this.onCropPointerDown);
      this.cropFrame?.addEventListener("lostpointercapture", this.onCropLostCapture);
      this.form?.addEventListener("submit", this.onSubmit, true);
      this.form?.addEventListener("variant:added", this.onVariantAdded);
      this.form?.addEventListener("variant:changed", this.onVariantChanged);
      this.form?.addEventListener("product:rerender", this.onProductRerender);
      window.addEventListener("resize", this.onResize);
      document.addEventListener("flickity:slide-settled", this.onSlideChange);
      document.addEventListener("flickity:slide-changed", this.onSlideChange);

      this.setProperty("_UploadType", this.dataset.uploadType);
      this.setProperty("_UploadVariantId", this.dataset.currentVariantId);
      this.confirmBusy = false;
      // Timer for the patience hint on long waits (see syncSlowHint).
      this.slowHintTimer = null;
      // True while the client-side "crop too small" message is showing — lets
      // updateCropPreview auto-clear exactly this message once the customer zooms out.
      this.cropTooSmallError = false;
      this.updateSubmitButtons();
      this.updateStickyBar();
    }

    disconnectedCallback() {
      this.fileInput?.removeEventListener("change", this.onFileChange);
      this.openButton?.removeEventListener("click", this.onOpenInline);
      this.cancelButton?.removeEventListener("click", this.onCancel);
      this.changeButton?.removeEventListener("click", this.onChange);
      this.chipEdit?.removeEventListener("click", this.onEdit);
      this.dropzone?.removeEventListener("click", this.onChooseFile);
      this.dropzone?.removeEventListener("dragenter", this.onDropzoneDragEnter);
      this.dropzone?.removeEventListener("dragover", this.onDropzoneDragEnter);
      this.dropzone?.removeEventListener("dragleave", this.onDropzoneDragLeave);
      this.dropzone?.removeEventListener("drop", this.onDropzoneDrop);
      this.resetButton?.removeEventListener("click", this.onReset);
      this.confirmButton?.removeEventListener("click", this.onConfirm);
      this.retryButton?.removeEventListener("click", this.onRetry);
      this.stickyAction?.removeEventListener("click", this.onStickyAction);
      this.cropScaleInput?.removeEventListener("input", this.onCropScaleInput);
      this.zoomInButton?.removeEventListener("click", this.onZoomIn);
      this.zoomOutButton?.removeEventListener("click", this.onZoomOut);
      this.rotateButton?.removeEventListener("click", this.onRotate);
      this.zonesToggle?.removeEventListener("click", this.onZonesToggle);
      this.cropFrame?.removeEventListener("keydown", this.onCropKeydown);
      this.cropFrame?.removeEventListener("pointerdown", this.onCropPointerDown);
      this.cropFrame?.removeEventListener("lostpointercapture", this.onCropLostCapture);
      window.removeEventListener("pointermove", this.onCropPointerMove);
      window.removeEventListener("pointerup", this.onCropPointerUp);
      window.removeEventListener("pointercancel", this.onCropPointerUp);
      this.activePointers?.clear();
      if (this.slowHintTimer) {
        clearTimeout(this.slowHintTimer);
        this.slowHintTimer = null;
      }
      this.form?.removeEventListener("submit", this.onSubmit, true);
      this.form?.removeEventListener("variant:added", this.onVariantAdded);
      this.form?.removeEventListener("variant:changed", this.onVariantChanged);
      this.form?.removeEventListener("product:rerender", this.onProductRerender);
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("flickity:slide-settled", this.onSlideChange);
      document.removeEventListener("flickity:slide-changed", this.onSlideChange);
      this.removePreview();
      this.revokeObjectUrl();
      this.revokeCroppedBlobUrl();
      this.revokeRotatedUrl();
      this.abortActiveSession();
    }

    get required() {
      return this.dataset.required === "true";
    }

    get endpointBaseUrl() {
      return (this.dataset.endpointBaseUrl || "").replace(/\/$/, "");
    }

    get maxBytes() {
      return Number(this.dataset.maxFileSizeMb || 80) * 1024 * 1024;
    }

    get acceptedTypes() {
      return (this.dataset.acceptedMimeTypes || "")
        .split(",")
        .map((type) => type.trim())
        .filter(Boolean);
    }

    parseMockupMap() {
      if (!this.dataset.mockupMapJson) return {};

      try {
        return JSON.parse(this.dataset.mockupMapJson);
      } catch (error) {
        console.warn("Hypemat uploader: invalid mockup map JSON", error);
        return {};
      }
    }

    createSession() {
      this.generation += 1;
      return {
        id: this.generation,
        controller: new AbortController(),
        file: null,
        uploadId: null,
        etag: null,
        // True once the storage PUT succeeded. Completion gates on THIS, never on session.etag:
        // the ETag header is null whenever the bucket CORS omits "Access-Control-Expose-Headers:
        // ETag", and gating on it silently wedged every upload in "processing".
        uploadFinished: false,
        completeRequestId: 0,
        completeInFlight: false,
        completeQueued: false,
        committedCrop: null,
      };
    }

    isCurrent(session) {
      return !!session && this.session === session && session.id === this.generation;
    }

    abortActiveSession() {
      if (this.session?.controller) {
        this.session.controller.abort();
      }
      window.clearTimeout(this.completeTimeout);
      this.completeTimeout = null;
    }

    delay(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    openInline() {
      // Reopening from the applied chip means "edit".
      if (this.state === STATE.APPLIED) {
        this.handleEdit();
        return;
      }

      this.clearError();
      if (this.fileInput) this.fileInput.value = ""; // NF5: let the same file be re-picked
      this.setState(STATE.CHOOSING);
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.dropzone?.focus();
      });
    }

    cancelToIdle() {
      // Backed out of the picker (e.g. the native file dialog was cancelled) — full reset,
      // and return focus to the opener so the keyboard user is never dumped on <body>.
      this.reset();
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.openButton?.focus();
      });
    }

    handleChange() {
      // Keep the configurator open, drop the current file, return to the picker.
      this.clearActiveUpload();
      this.file = null;
      this.declaredType = "";
      if (this.fileInput) this.fileInput.value = "";
      if (this.cropElement) this.cropElement.hidden = true;
      if (this.previewEmpty) this.previewEmpty.hidden = false;
      if (this.fileMeta) this.fileMeta.hidden = true;
      this.resetCrop();
      delete this.dataset.fileKind;
      this.clearError();
      this.setState(STATE.CHOOSING);
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.dropzone?.focus();
      });
    }

    handleEdit() {
      // Re-open from the applied chip to adjust the crop (or re-pick if no file is held).
      this.clearError();
      // Drop any debounced crop-complete carried over from the previous crop session.
      window.clearTimeout(this.completeTimeout);
      this.completeTimeout = null;

      if (this.file && this.objectUrl) {
        // Make sure the crop surface is actually shown (a prior error path may have hidden it).
        if (this.cropElement) this.cropElement.hidden = false;
        if (this.previewEmpty) this.previewEmpty.hidden = true;
        this.updateCropZones();
        this.setState(STATE.READY);
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          // Focus the crop frame when it is visible, else the always-present change button.
          const target = this.cropElement && !this.cropElement.hidden ? this.cropFrame : this.changeButton;
          target?.focus();
        });
      } else {
        this.openInline();
      }
    }

    async handleConfirm() {
      const session = this.session;
      if (this.state !== STATE.READY || !session) return;

      this.clearError();

      // Client-side echo of the backend's minimum-crop check, BEFORE the flush renders: a
      // too-deep zoom is answered instantly and never wastes the render slot. False "too small"
      // is impossible from downscaled display bakes (4096 cap / max zoom 3 stays far above the
      // 100 px limit); genuinely small originals are uncapped and measure exactly. The server
      // check remains the backstop.
      if (this.imageMetrics) {
        const cropRect = this.computeCropRect();
        if (cropRect.width < MIN_CROP_EDGE_PIXELS || cropRect.height < MIN_CROP_EDGE_PIXELS) {
          this.showError(this.getImageTooSmallMessage());
          this.cropTooSmallError = true;
          return;
        }
      }

      this.setConfirmLoading(true);
      try {
        // Commit the current crop server-side BEFORE freeing add-to-cart. Since render-on-confirm
        // this flush IS the crop render (interactions no longer pre-render); an untouched crop
        // still matches committedCrop from the initial /complete and collapses instantly.
        await this.flushPendingComplete();
      } catch (error) {
        this.setConfirmLoading(false);
        if (error?.name === "AbortError") return;
        this.setState(STATE.ERROR);
        this.failUpload(this.getErrorMessage(error), error);
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          // On a retryable failure the retry button is the customer's next step; a fatal one
          // leaves only "pick another file".
          const target = this.retryButton && !this.retryButton.hidden ? this.retryButton : this.changeButton;
          target?.focus();
        });
        return;
      }

      // /complete and the client rotation bake run independently; do not collapse while the
      // crop frame can still be showing the pre-rotation bitmap. Capped so a stuck canvas path
      // leaves the UI retryable instead of hanging the apply button forever.
      let rotationWaited = 0;
      while (this.rotationBakePending && this.isCurrent(session) && this.state === STATE.READY && rotationWaited < 5000) {
        await this.delay(50);
        rotationWaited += 50;
      }
      if (this.rotationBakePending && this.isCurrent(session) && this.state === STATE.READY) {
        console.warn("Hypemat uploader: rotation bake did not finish before confirm");
        this.setConfirmLoading(false);
        return;
      }
      if (!this.isCurrent(session) || this.state !== STATE.READY) {
        this.setConfirmLoading(false);
        return;
      }

      // Make sure the motif is actually painted before collapsing, then reveal it. Keep the
      // confirm button in its loading/disabled state until APPLIED so a double-click during the
      // paint wait cannot re-enter the apply flow.
      await this.ensurePreviewPainted();
      if (!this.isCurrent(session) || this.state !== STATE.READY) {
        this.setConfirmLoading(false);
        return;
      }
      this.collapseToChip();
      this.setConfirmLoading(false);
    }

    async flushPendingComplete() {
      const session = this.session;
      if (!session || !session.uploadId || !session.uploadFinished) return;

      // Cancel any debounced crop-complete, then drive completes until the server has committed
      // EXACTLY the crop on screen (committedCrop === current crop) and nothing is in flight.
      // This guarantees the production raster matches the preview before add-to-cart is freed,
      // regardless of how slow a single /complete is.
      window.clearTimeout(this.completeTimeout);
      this.completeTimeout = null;

      for (let pass = 0; pass < 60; pass += 1) {
        let waited = 0;
        while (session.completeInFlight && this.isCurrent(session) && waited < 600) {
          await this.delay(50);
          waited += 1;
        }
        if (!this.isCurrent(session)) return;
        if (this.cropMatchesCommitted(session)) return;

        session.completeRequestId += 1;
        await this.completeUpload(session, session.completeRequestId);
      }
    }

    cropMatchesCommitted(session) {
      const committed = session?.committedCrop;
      if (!committed) return false;
      return (
        this.formatCropNumber(committed.x) === this.formatCropNumber(this.crop.x) &&
        this.formatCropNumber(committed.y) === this.formatCropNumber(this.crop.y) &&
        this.formatCropNumber(committed.scale) === this.formatCropNumber(this.crop.scale) &&
        Number(committed.rotation || 0) === Number(this.crop.rotation || 0)
      );
    }

    collapseToChip() {
      const isPdf = this.isPdfFile();
      if (this.chipName) this.chipName.textContent = this.file?.name || "";
      this.renderChipMeta();

      if (isPdf) {
        if (this.chipThumb) this.chipThumb.hidden = true;
        if (this.chipIcon) {
          this.chipIcon.hidden = false;
          this.chipIcon.textContent = "PDF";
        }
      } else {
        if (this.chipThumb) {
          this.chipThumb.hidden = false;
          this.setChipThumbnail();
        }
        if (this.chipIcon) this.chipIcon.hidden = true;
      }

      this.setState(STATE.APPLIED);

      // The collapse changes layout — reposition the overlay, bring the motif into view, move focus.
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.updateMediaPreviewLayout();
        this.scrollToMedia();
        this.focusAfterApply();
      });
    }

    // Print-file proof for the chip ("7441 × 4430 px · 12.4 MB") and the visible line-item
    // property that carries the same text into cart, checkout and order mail. Dimensions only when
    // the browser decoded the original itself (sourceDimensions); PDF/HEIC/SVG show size only.
    renderChipMeta() {
      const parts = [];
      const format = this.dataset.chipDimensionsFormat || "";
      if (this.sourceDimensions && format) {
        parts.push(
          format
            .replace("[width]", String(this.sourceDimensions.width))
            .replace("[height]", String(this.sourceDimensions.height))
        );
      }
      const size = this.formatBytes(this.file?.size);
      if (size) parts.push(size);

      const text = parts.join(" · ");
      if (this.chipMetaText) this.chipMetaText.textContent = text;
      if (this.chipMeta) this.chipMeta.hidden = !text;

      const fileName = this.file?.name || "";
      this.setProperty("PrintFile", [fileName, text].filter(Boolean).join(" · "));
    }

    clearChipMeta() {
      if (this.chipMetaText) this.chipMetaText.textContent = "";
      if (this.chipMeta) this.chipMeta.hidden = true;
    }

    scrollToMedia() {
      const wrapper = this.getSelectedMediaWrapper();
      const target = wrapper?.closest("product-media") || wrapper;
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }

    focusAfterApply() {
      const activeSubmit = this.getSubmitButtons().find((button) => !button.disabled);
      if (activeSubmit) {
        activeSubmit.focus();
        return;
      }
      this.chipEdit?.focus();
    }

    setConfirmLoading(loading) {
      // Track the applying state on the host so the mobile sticky action can mirror it (a
      // second tap during apply must not re-enter the flow). Set before the confirm-button
      // guard so a missing inline button never desyncs the sticky bar.
      this.confirmBusy = loading;
      this.syncSlowHint();

      if (this.confirmButton) {
        this.confirmButton.classList.toggle("is-loading", loading);
        this.confirmButton.setAttribute("aria-busy", loading ? "true" : "false");

        const label = this.confirmButton.querySelector("[data-hypemat-confirm-label]");
        if (label) {
          if (loading) {
            if (label.dataset.defaultLabel == null) label.dataset.defaultLabel = label.textContent;
            if (this.dataset.applyingText) label.textContent = this.dataset.applyingText;
          } else if (label.dataset.defaultLabel != null) {
            label.textContent = label.dataset.defaultLabel;
          }
        }

        this.confirmButton.disabled = loading || this.state !== STATE.READY;
      }

      this.updateStickyBar();
    }

    ensurePreviewPainted() {
      // Wait until the gallery motif is decoded so it is visible the moment the dialog closes,
      // while keeping the applying state on screen long enough to register. Capped so we never hang.
      const minVisible = this.delay(400);
      const artwork = document.querySelector("[data-hypemat-preview-artwork]");
      const decoded =
        artwork && typeof artwork.decode === "function" ? artwork.decode().catch(() => {}) : Promise.resolve();

      return Promise.race([Promise.all([minVisible, decoded]), this.delay(5000)]);
    }

    async handleFileChange(event) {
      const [file] = event.target.files || [];
      if (!file) return;

      await this.handleSelectedFile(file);
    }

    chooseFile() {
      this.fileInput?.click();
    }

    handleDropzoneDragEnter(event) {
      event.preventDefault();
      this.dataset.dragging = "true";
    }

    handleDropzoneDragLeave(event) {
      if (event.relatedTarget && this.dropzone?.contains(event.relatedTarget)) return;
      this.dataset.dragging = "false";
    }

    async handleDropzoneDrop(event) {
      event.preventDefault();
      this.dataset.dragging = "false";

      const [file] = event.dataTransfer?.files || [];
      if (!file) return;

      await this.handleSelectedFile(file);
    }

    async handleSelectedFile(file) {
      this.clearError();
      this.hideIntroHint();
      this.clearActiveUpload();
      const session = this.createSession();
      this.session = session;
      session.file = file;
      this.file = file;
      this.declaredType = this.resolveUploadType(file);
      session.declaredType = this.declaredType;
      this.dataset.fileKind = this.isPdfFile() ? "pdf" : this.isHeicFile() ? "heic" : "image";
      this.setFileMeta(file);
      this.clearProperties();

      const validationError = this.validateFile(file);
      if (validationError) {
        this.setState(STATE.ERROR);
        this.failUpload(validationError);
        if (this.cropElement) this.cropElement.hidden = true;
        if (this.previewEmpty) this.previewEmpty.hidden = false;
        return;
      }

      this.setState(STATE.UPLOADING);
      if (this.needsServerRaster()) {
        // PDF/HEIC can't preview locally — show the pending state; the crop UI appears once /prepare
        // returns a server raster we can load into the crop frame.
        this.createPendingServerPreview();
      } else {
        this.createLocalPreview(file, session);
      }

      try {
        await this.uploadFile(session);
      } catch (error) {
        if (!this.isCurrent(session) || error.name === "AbortError") return;
        console.error("Hypemat uploader: upload failed", error);
        this.setState(STATE.ERROR);
        this.failUpload(this.getErrorMessage(error), error);
      }
    }

    validateFile(file) {
      // Validate the RESOLVED type, not the raw file.type — HEIC often arrives with an empty type and
      // must still pass when the extension identifies it.
      const type = this.declaredType || this.resolveUploadType(file);
      if (this.acceptedTypes.length > 0 && !this.acceptedTypes.includes(type)) {
        return this.dataset.invalidTypeMessage || "Bitte lade eine JPG-, PNG-, WebP-, HEIC- oder PDF-Datei hoch.";
      }

      if (file.size > this.maxBytes) {
        return this.dataset.maxSizeMessage || `Die Datei darf maximal ${this.dataset.maxFileSizeMb || 80} MB gross sein.`;
      }

      return "";
    }

    async uploadFile(session) {
      this.setState(STATE.UPLOADING);
      this.setStatus(this.dataset.uploadingText || "Bild wird hochgeladen...");

      // Declared MIME is the canonicalized type (session.declaredType), not the raw file.type, so
      // HEIC uploads carry "image/heic" through the presigned PUT and match backend detection.
      const declaredType = session.declaredType || session.file.type;
      const createResponse = await this.fetchJson(`${this.endpointBaseUrl}/v1/uploads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productId: this.dataset.productGid,
          variantId: this.getCurrentVariantId(),
          productHandle: this.dataset.productHandle,
          uploadType: this.dataset.uploadType,
          file: {
            name: session.file.name,
            size: session.file.size,
            type: declaredType,
          },
        }),
        signal: session.controller.signal,
      });
      if (!this.isCurrent(session)) return;

      const upload = createResponse.upload || {};
      session.uploadId = createResponse.uploadId;
      // Timestamp for the beacon's put_ms marker: how long the S3 PUT ran before it failed
      // separates "blocked instantly" (extension/proxy, <~500 ms) from "died mid-transfer"
      // (network) — the error log cannot tell the two apart otherwise.
      session.putStartedAt = Date.now();
      const uploadResponse = await fetch(upload.url, {
        method: upload.method || "PUT",
        headers: upload.headers || {
          "Content-Type": declaredType,
        },
        body: session.file,
        signal: session.controller.signal,
      });
      if (!this.isCurrent(session)) return;

      if (!uploadResponse.ok) {
        const error = new Error(`Storage upload failed with ${uploadResponse.status}`);
        error.status = uploadResponse.status;
        throw error;
      }

      this.setState(STATE.PROCESSING);
      // ETag is informational only — the backend stores it but never validates it, and reading it
      // yields null when the bucket CORS does not expose the header. uploadFinished is the real
      // "PUT succeeded" marker that frees /prepare + /complete.
      session.etag = uploadResponse.headers.get("ETag") || "";
      session.uploadFinished = true;

      // PDF/HEIC: fetch the server-rendered full raster and load it into the crop UI BEFORE the first
      // /complete, so the customer crops the same pixels the backend will crop. Images skip this.
      if (this.needsServerRaster()) {
        await this.prepareServerRaster(session);
        if (!this.isCurrent(session) || this.state === STATE.ERROR) return;
      }

      await this.completeUpload(session);
    }

    async prepareServerRaster(session) {
      const response = await this.fetchJson(
        `${this.endpointBaseUrl}/v1/uploads/${encodeURIComponent(session.uploadId)}/prepare`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ etag: session.etag }),
          signal: session.controller.signal,
        }
      );
      if (!this.isCurrent(session)) return;

      await this.loadServerCropSource(response.fullPageUrl, session);
    }

    async completeUpload(session, requestId = null) {
      if (!session || !session.uploadId || !session.uploadFinished) return;

      const completeRequestId = requestId || (session.completeRequestId += 1);
      if (!this.isCurrent(session) || completeRequestId !== session.completeRequestId) return;
      if (session.completeInFlight) {
        session.completeQueued = true;
        return;
      }

      session.completeInFlight = true;
      try {
        this.setState(STATE.PROCESSING);
        this.setCropProperties();
        const snapshot = {
          uploadId: session.uploadId,
          etag: session.etag,
          crop: {
            x: this.crop.x,
            y: this.crop.y,
            scale: this.crop.scale,
            rotation: this.crop.rotation,
            aspectRatio: this.crop.aspectRatio,
          },
        };

        const completeResponse = await this.fetchJson(
          `${this.endpointBaseUrl}/v1/uploads/${encodeURIComponent(snapshot.uploadId)}/complete`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              etag: snapshot.etag,
              variantId: this.getCurrentVariantId(),
              crop: snapshot.crop,
            }),
            signal: session.controller.signal,
          }
        );

        if (!this.isCurrent(session) || completeRequestId !== session.completeRequestId) return;

        this.applyLineItemProperties(completeResponse.lineItemProperties || {});
        this.setProperty("_UploadVariantId", this.getCurrentVariantId());
        this.setMockupProperties();
        this.setCropProperties();
        // Record the crop the server just rendered so a confirm can verify the production raster
        // matches what is on screen before freeing add-to-cart.
        session.committedCrop = snapshot.crop;

        // No server-preview reload here: PDF/HEIC already hold the full crop raster (loaded via
        // /prepare in loadServerCropSource) and crop it client-side exactly like an image, so the
        // production raster stays in sync without swapping the crop frame's image.

        this.clearError();
        this.setState(STATE.READY);
        this.setStatus(this.dataset.successText || "Bild ist bereit.");
      } catch (error) {
        if (!this.isCurrent(session) || completeRequestId !== session.completeRequestId || error.name === "AbortError") return;
        throw error;
      } finally {
        session.completeInFlight = false;
        if (this.isCurrent(session) && session.completeQueued) {
          session.completeQueued = false;
          const queuedRequestId = session.completeRequestId;
          this.completeUpload(session, queuedRequestId).catch((error) => {
            if (!this.isCurrent(session) || queuedRequestId !== session.completeRequestId || error.name === "AbortError") return;
            console.error("Hypemat uploader: queued crop processing failed", error);
            this.setState(STATE.ERROR);
            this.failUpload(this.getErrorMessage(error), error);
          });
        }
      }
    }

    async fetchJson(url, options) {
      if (!this.endpointBaseUrl) {
        throw new Error("Missing upload endpoint base URL");
      }

      const response = await fetch(url, options);
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(body.message || `Request failed with ${response.status}`);
        error.code = body.code || "";
        error.status = response.status;
        throw error;
      }

      return body;
    }

    applyLineItemProperties(properties) {
      Object.entries(properties).forEach(([key, value]) => {
        this.setProperty(key, value);
      });
    }

    setProperty(key, value) {
      const input = this.querySelector(`[data-hypemat-property="${CSS.escape(key)}"]`);
      if (!input) return;
      input.value = value || "";
      // Empty properties would still be submitted and show up as blank rows in checkout and
      // order admin (e.g. _UploadOriginalPdfUrl on image uploads) — disabled inputs are skipped
      // by form serialization. getProperty reads .value directly, so reads are unaffected.
      input.disabled = !input.value;
    }

    getProperty(key) {
      return this.querySelector(`[data-hypemat-property="${CSS.escape(key)}"]`)?.value || "";
    }

    getCurrentVariantId() {
      const idInput = this.form?.querySelector('[name="id"]');
      return idInput?.value || this.dataset.currentVariantId || "";
    }

    handleVariantChanged(event) {
      const variantId = event.detail?.variant?.id;
      if (!variantId) return;

      this.dataset.currentVariantId = String(variantId);
      this.setProperty("_UploadVariantId", variantId);
      this.setMockupProperties();
      this.applyPreview();
      // Zone styles are variants — the crop overlay has to follow the switch immediately.
      this.updateCropZones();
      // Browsing zone styles before uploading is legitimate; a friendly status line (not an
      // error) explains why nothing visibly changes yet.
      if (this.required && (this.state === STATE.IDLE || this.state === STATE.CHOOSING)) {
        this.showIntroHint();
      } else {
        this.hideIntroHint();
      }
      // The theme's own variant handling may re-enable the ATC button (e.g. switching from a
      // sold-out variant back to an available one) — re-assert the lock state after it ran.
      requestAnimationFrame(() => this.updateSubmitButtons());
    }

    handleProductRerender() {
      requestAnimationFrame(() => {
        this.applyPreview();
        this.updateSubmitButtons();
      });
    }

    handleResize() {
      // Coalesce resize bursts (mobile address-bar show/hide fires resize on every scroll tick)
      // into one rAF doing a CHEAP relayout — the expensive canvas re-encode no longer lives here.
      if (this._resizeRaf) return;
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = null;
        if (!this.isConnected) return;

        const width = window.innerWidth;
        const widthChanged = width !== this._lastResizeWidth;
        this._lastResizeWidth = width;

        // Crop frame is vh-capped (width: min(100%, calc(46vh * aspect via --hm-mat-aspect))), so a height-only resize
        // (desktop bottom-edge drag, docked devtools) can change ITS width while window width is
        // stable. Key its relayout off its own measured width — unchanged on iOS address-bar
        // scroll (vh is pinned to the large viewport there), so that path stays a no-op.
        if (this.cropImage && this.cropElement && !this.cropElement.hidden) {
          const frameWidth = this.cropFrame ? this.cropFrame.getBoundingClientRect().width : 0;
          if (frameWidth && frameWidth !== this._lastFrameWidth) {
            this._lastFrameWidth = frameWidth;
            this.applyCropStyles(this.cropImage, null, this.imageMetrics ? this.computeCropRect() : null);
            this.positionCropZones();
          }
        }

        // The gallery overlay is width-responsive (not vh), so only relayout it on a real window
        // width change — this keeps iOS address-bar scrolling free of overlay work.
        if (widthChanged) this.updateMediaPreviewLayout();
      });
    }

    handleSlideChange() {
      if (!this.objectUrl) return;

      const wrapper = this.getSelectedMediaWrapper();
      if (!wrapper) return;

      // Already hosting the overlay (e.g. the settle event following a change we handled) —
      // just re-position the clip against the settled (final adaptiveHeight) wrapper size.
      if (wrapper.querySelector("[data-hypemat-media-preview]")) {
        this.updateMediaPreviewLayout();
        return;
      }

      this.applyPreview();
    }

    handleVariantAdded(event) {
      const uploadId = this.getProperty("_UploadId");
      if (!uploadId || !this.endpointBaseUrl) return;

      const added = event?.detail?.variant;
      if (added && !this.matchesThisUpload(added, uploadId)) return;

      this.attachCart({
        uploadId,
        variantId: String(added?.variant_id || added?.id || this.getCurrentVariantId()),
        generation: this.generation,
      });
    }

    matchesThisUpload(added, uploadId) {
      const addedProperties = added && typeof added.properties === "object" ? added.properties : null;
      if (addedProperties) {
        return String(addedProperties._UploadId || "") === String(uploadId || "");
      }

      const ourProductId = String(this.dataset.productId || "");
      const addedProductId = String(added.product_id || "");
      if (ourProductId && addedProductId) return ourProductId === addedProductId;

      return true;
    }

    async attachCart({ uploadId, variantId, generation }) {
      const core = this.getCore();
      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        let status = 0;
        try {
          const response = await fetch(`${this.endpointBaseUrl}/v1/uploads/${encodeURIComponent(uploadId)}/attach-cart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              variantId,
            }),
          });
          status = response.status;
        } catch (error) {
          status = 0;
        }

        const result = core.classifyAttachCartResult(status);
        if (result === "success") {
          if (this.generation === generation && this.getProperty("_UploadId") === uploadId) {
            this.reset();
          }
          return;
        }

        if (attempt < maxAttempts) {
          await this.delay(core.attachRetryDelayMs(attempt));
        }
      }

      console.warn("Hypemat uploader: cart attachment failed after retries", uploadId);
      // Silent-degradation report: the cart add succeeded for the customer, only the backend
      // status linkage failed — exactly the kind of failure nobody would ever tell support about.
      this.reportClientError("attach_cart_failed", null, { uploadId });
      if (this.generation === generation && this.getProperty("_UploadId") === uploadId) {
        this.showError(this.dataset.attachErrorMessage || this.dataset.errorText || "Upload fehlgeschlagen. Bitte erneut versuchen.");
      }
    }

    handleSubmit(event) {
      // ATC gate: only an APPLIED upload (consciously confirmed) may add to cart, never a
      // mid-crop READY one — otherwise an unconfirmed motif could be purchased. The submit
      // buttons are aria-disabled instead of natively disabled (see updateSubmitButtons), so
      // this gate is where a blocked click gets its answer: a state-specific message plus
      // focus back to the uploader.
      const busy = this.state === STATE.UPLOADING || this.state === STATE.PROCESSING;
      const blocked = (this.required && this.state !== STATE.APPLIED) || busy;
      if (!blocked) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      let message = this.dataset.requiredMessage || "Bitte lade zuerst ein Bild hoch.";
      if (busy) {
        message = this.dataset.stillProcessingMessage || message;
      } else if (this.state === STATE.READY) {
        message = this.dataset.confirmFirstMessage || message;
      }
      this.showError(message);
      this.focus();
    }

    handleCropScaleInput(event) {
      if (!this.objectUrl || !this.imageMetrics) return;

      this.crop.scale = Number(event.target.value || 1);
      this.updateCropPreview();
      this.maybeScheduleErrorRecovery();
    }

    // Keyboard crop (BFSG / WCAG 2.1.1): arrows nudge the image, +/- zoom. Same normalized 0..1
    // model as pointer/pinch, so computeCropRect() handles scale-aware edge clamping for free.
    handleCropKeydown(event) {
      if (!this.objectUrl || !this.imageMetrics) return;
      const step = event.shiftKey ? 0.1 : 0.02;
      switch (event.key) {
        case "ArrowLeft": this.nudgePosition(step, 0); break;
        case "ArrowRight": this.nudgePosition(-step, 0); break;
        case "ArrowUp": this.nudgePosition(0, step); break;
        case "ArrowDown": this.nudgePosition(0, -step); break;
        case "+":
        case "=": this.nudgeScale(0.1); break;
        case "-":
        case "_": this.nudgeScale(-0.1); break;
        case "r":
        case "R": this.rotateClockwise(); break;
        default: return;
      }
      event.preventDefault();
    }

    nudgePosition(dx, dy) {
      if (!this.objectUrl || !this.imageMetrics) return;
      const nextX = this.clamp(this.crop.x + dx, 0, 1);
      const nextY = this.clamp(this.crop.y + dy, 0, 1);
      if (nextX === this.crop.x && nextY === this.crop.y) return;
      this.crop.x = nextX;
      this.crop.y = nextY;
      this.updateCropPreview();
      this.maybeScheduleErrorRecovery();
    }

    nudgeScale(delta) {
      if (!this.objectUrl || !this.imageMetrics) return;
      const next = this.clamp(this.crop.scale + delta, 1, 3);
      if (next === this.crop.scale) return;
      this.crop.scale = next;
      this.updateCropPreview();
      this.maybeScheduleErrorRecovery();
    }

    rotateClockwise() {
      if (!this.objectUrl || !this.imageMetrics) return;

      this.crop.rotation = (this.crop.rotation + 90) % 360;
      // Follow the customer's focus point through the turn: a point at (x, y) lands at
      // (1 - y, x) after a clockwise quarter turn. Scale keeps; the frame aspect does not
      // rotate, so the exact visible region cannot be preserved — the focus point is.
      const previousX = this.crop.x;
      this.crop.x = this.clamp(1 - this.crop.y, 0, 1);
      this.crop.y = this.clamp(previousX, 0, 1);

      this.setCropProperties();
      this.applyRotation();
      // No eager server render (render-on-confirm) — the confirm flush reads this.crop at fire
      // time and carries the rotation; the bake-pending wait in handleConfirm still applies.
      this.maybeScheduleErrorRecovery();
    }

    // Bake the rotated working bitmap for the crop UI. Display-only: /complete re-renders the
    // ORIGINAL server-side with the same rotation, so this bake may downscale (long edge capped
    // like the server crop-source) without any print-quality cost. Always drawn from the pristine
    // base image — never from an already-rotated bake — so repeated turns do not accumulate
    // re-encode loss. The cropImage onload path then swaps imageMetrics to the rotated dimensions
    // and the entire crop engine works unchanged.
    applyRotation() {
      const session = this.session;
      const token = ++this.rotationToken;
      const rotation = this.crop.rotation;
      this.rotationBakePending = true;

      if (rotation === 0) {
        if (this.cropImage && this.objectUrl) {
          this.cropImage.crossOrigin = this.objectUrlIsLocal ? null : "anonymous";
          this.cropImage.src = this.objectUrl;
        }
        this.revokeRotatedUrl();
        this.rotationBakePending = false;
        return;
      }

      this.loadRotationSource()
        .then((source) => {
          if (token !== this.rotationToken || !this.isCurrent(session)) {
            this.clearRotationBakePending(token);
            return;
          }

          // Cap the working bitmap so the canvas stays inside conservative mobile limits —
          // same bound as the server crop-source (crop values are resolution-independent).
          const maxEdge = 4096;
          const downscale = Math.min(1, maxEdge / Math.max(source.naturalWidth, source.naturalHeight));
          const drawWidth = Math.max(1, Math.round(source.naturalWidth * downscale));
          const drawHeight = Math.max(1, Math.round(source.naturalHeight * downscale));
          const swapped = rotation === 90 || rotation === 270;

          const canvas = document.createElement("canvas");
          canvas.width = swapped ? drawHeight : drawWidth;
          canvas.height = swapped ? drawWidth : drawHeight;
          const context = canvas.getContext("2d");
          if (!context || !canvas.toBlob) {
            this.clearRotationBakePending(token);
            this.rollbackRotation();
            return;
          }

          context.translate(canvas.width / 2, canvas.height / 2);
          context.rotate((rotation * Math.PI) / 180);
          context.drawImage(source, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

          // JPEG sources re-encode as JPEG; everything else keeps PNG so alpha survives —
          // a transparent logo must preview like it prints.
          const isJpeg = (this.declaredType || "").toLowerCase() === "image/jpeg";
          canvas.toBlob(
            (blob) => {
              if (token !== this.rotationToken || !this.isCurrent(session)) {
                this.clearRotationBakePending(token);
                return;
              }
              if (!blob || !this.cropImage) {
                this.clearRotationBakePending(token);
                this.rollbackRotation();
                return;
              }
              this.revokeRotatedUrl();
              this.rotatedUrl = URL.createObjectURL(blob);
              this.cropImage.crossOrigin = null; // blob: URLs are same-origin
              this.cropImage.src = this.rotatedUrl;
              this.rotationBakePending = false;
            },
            isJpeg ? "image/jpeg" : "image/png",
            0.92
          );
        })
        .catch(() => {
          if (token !== this.rotationToken || !this.isCurrent(session)) {
            this.clearRotationBakePending(token);
            return;
          }
          this.rollbackRotation();
        });
    }

    clearRotationBakePending(token) {
      // Stale rotation callbacks must not clear a newer bake's pending flag.
      if (token === this.rotationToken) this.rotationBakePending = false;
    }

    handleZonesToggle() {
      this.zonesVisible = !this.zonesVisible;
      this.updateCropZones();
    }

    // Zone overlay in the crop frame — a display-only alignment layer. It reuses the SAME square
    // mockup PNG the gallery composites and the SAME mapping (square drawn at frame width,
    // centered — the print band lands exactly on the frame), so crop view, gallery preview and
    // the delivered mat always agree. Never touches properties or the server payload.
    updateCropZones() {
      if (!this.cropZones) return;

      const url = this.getMockupUrl();
      const available = Boolean(url);
      const show = available && this.zonesVisible;

      if (this.zonesToggle) {
        // No overlay for this variant/format (e.g. mousepads) — no toggle to offer.
        this.zonesToggle.hidden = !available;
        this.zonesToggle.setAttribute("aria-pressed", this.zonesVisible ? "true" : "false");
        const label = this.zonesVisible ? this.dataset.zonesHideLabel : this.dataset.zonesShowLabel;
        if (label) {
          this.zonesToggle.setAttribute("aria-label", label);
          this.zonesToggle.title = label;
        }
      }

      const nextImage = show ? `url("${url}")` : "";
      if (this.cropZones.style.backgroundImage !== nextImage) {
        this.cropZones.style.backgroundImage = nextImage;
      }
      this.cropZones.hidden = !show;
      this.positionCropZones();
    }

    // Map the square overlay PNG onto the crop frame. Without an artwork frame the CSS default
    // (frame width, centred) is right: the 1p mockups run edge to edge, so their print band IS
    // the frame. With a frame (mousepad: mockup keeps a margin around the pad) the overlay has
    // to be scaled up and shifted so that its artwork band lands on the frame — otherwise the
    // mockup's opaque margin paints over the motif's edges and the crop looks tighter than the
    // gallery/production (live case 2026-09-03).
    positionCropZones() {
      if (!this.cropZones || this.cropZones.hidden) return;
      const frame = this.artworkFrame;
      if (!frame || !this.cropFrame) {
        this.cropZones.style.backgroundSize = "";
        this.cropZones.style.backgroundPosition = "";
        return;
      }
      const frameWidth = this.cropFrame.getBoundingClientRect().width;
      if (!frameWidth) return;
      const side = frameWidth / frame.width; // rendered size of the square overlay
      this.cropZones.style.backgroundSize = `${side}px ${side}px`;
      this.cropZones.style.backgroundPosition = `${-frame.left * side}px ${-frame.top * side}px`;
    }

    showIntroHint() {
      if (!this.introHint) return;
      const message = this.dataset.variantHintMessage || "";
      if (!message) return;
      // The intro error line has priority — the hint only speaks when nothing else does.
      if (this.introError && !this.introError.hidden) return;
      this.introHint.textContent = message;
      this.introHint.hidden = false;
    }

    hideIntroHint() {
      if (!this.introHint) return;
      this.introHint.textContent = "";
      this.introHint.hidden = true;
    }

    // The display bake failed (decode/canvas limit) — roll the model back one quarter turn so
    // screen, properties and the server-rendered production file never disagree. A /complete that
    // already fired with the failed rotation is corrected by the re-schedule below (and
    // flushPendingComplete blocks apply until committed === current either way).
    // Deliberately still schedules eagerly under render-on-confirm: a confirm may have flushed
    // the failed rotation already and be waiting on this very bake — the synchronous PROCESSING
    // from the schedule aborts that confirm at its READY-recheck before it can collapse with a
    // production file that no longer matches the screen.
    rollbackRotation() {
      console.warn("Hypemat uploader: rotation bake failed, reverting");
      // Silent-degradation report: the customer just sees the rotation snap back, no error box.
      this.reportClientError("rotation_bake_rollback");
      this.rotationBakePending = false;
      this.crop.rotation = (this.crop.rotation + 270) % 360;
      const previousY = this.crop.y;
      this.crop.y = this.clamp(1 - this.crop.x, 0, 1);
      this.crop.x = this.clamp(previousY, 0, 1);
      this.setCropProperties();
      this.updateCropPreview();
      this.scheduleCompleteUpload();
    }

    // Decode (once per file) the pristine base image the rotation bake draws from: the local
    // object URL, or the server crop-source for PDF/HEIC (crossorigin keeps the canvas clean).
    loadRotationSource() {
      if (!this.objectUrl) return Promise.reject(new Error("No rotation source"));
      if (
        this.rotationSourceUrl === this.objectUrl &&
        this.rotationSourceImage?.complete &&
        this.rotationSourceImage.naturalWidth
      ) {
        return Promise.resolve(this.rotationSourceImage);
      }

      const sourceUrl = this.objectUrl;
      const sourceIsLocal = this.objectUrlIsLocal;
      return new Promise((resolve, reject) => {
        const image = new Image();
        if (!sourceIsLocal) image.crossOrigin = "anonymous";
        image.onload = () => {
          if (this.objectUrl !== sourceUrl) {
            reject(new Error("Rotation source changed"));
            return;
          }
          this.rotationSourceImage = image;
          this.rotationSourceUrl = sourceUrl;
          resolve(image);
        };
        image.onerror = () => reject(new Error("Rotation source failed to load"));
        image.src = sourceUrl;
      });
    }

    revokeRotatedUrl() {
      if (this.rotatedUrl) {
        URL.revokeObjectURL(this.rotatedUrl);
        this.rotatedUrl = null;
      }
    }

    // Drop everything derived from the current base image (called whenever the base is discarded
    // via revokeObjectUrl): invalidate in-flight bakes, free the baked blob, forget the source.
    clearRotationArtifacts() {
      this.rotationToken += 1;
      this.revokeRotatedUrl();
      this.rotationSourceImage = null;
      this.rotationSourceUrl = null;
      this.rotationBakePending = false;
    }

    handleCropPointerDown(event) {
      if (!this.objectUrl || !this.imageMetrics) return;

      event.preventDefault();
      this.cropFrame.setPointerCapture?.(event.pointerId);
      this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.activePointers.size === 1) {
        // First finger down — open the gesture and listen on window so a drag that leaves the
        // frame still tracks. Re-attach idempotently in case a prior gesture's final up/cancel was
        // lost, so listeners never accumulate and stale state never leaks into this gesture.
        window.removeEventListener("pointermove", this.onCropPointerMove);
        window.removeEventListener("pointerup", this.onCropPointerUp);
        window.removeEventListener("pointercancel", this.onCropPointerUp);
        window.addEventListener("pointermove", this.onCropPointerMove);
        window.addEventListener("pointerup", this.onCropPointerUp);
        window.addEventListener("pointercancel", this.onCropPointerUp);
        this.cropMoved = false;
        this.gestureMultiTouch = false;
        this.pointerDownInfo = { time: event.timeStamp, x: event.clientX, y: event.clientY };
        this.beginCropDrag(event.clientX, event.clientY);
      } else {
        // Second (or additional) finger down — switch to / re-seed pinch-zoom against the current
        // scale so adding a finger never jumps. Slider/buttons stay equivalent.
        this.gestureMultiTouch = true;
        this.beginCropPinch();
      }
    }

    beginCropDrag(clientX, clientY) {
      this.pinch = null;
      this.dragStart = {
        pointerX: clientX,
        pointerY: clientY,
        cropX: this.crop.x,
        cropY: this.crop.y,
        cropRect: this.computeCropRect(),
      };
    }

    beginCropPinch() {
      const points = Array.from(this.activePointers.values());
      this.dragStart = null;
      this.pinch = {
        initialDistance: this.pointerDistance(points[0], points[1]) || 1,
        initialScale: this.crop.scale,
      };
    }

    pointerDistance(a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    handleCropPointerMove(event) {
      if (!this.activePointers.has(event.pointerId)) return;
      this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pinch && this.activePointers.size >= 2) {
        // Pinch zoom around the current crop center (consistent with the slider). The normalized
        // model means computeCropRect() handles scale-dependent edge clamping for free.
        const points = Array.from(this.activePointers.values());
        const ratio = this.pointerDistance(points[0], points[1]) / this.pinch.initialDistance;
        const nextScale = this.clamp(this.pinch.initialScale * ratio, 1, 3);
        if (nextScale !== this.crop.scale) {
          this.crop.scale = nextScale;
          this.cropMoved = true;
          this.updateCropPreview();
        }
        return;
      }

      if (!this.dragStart || !this.cropFrame) return;

      const rect = this.cropFrame.getBoundingClientRect();
      const cropRect = this.dragStart.cropRect;
      const deltaX = ((event.clientX - this.dragStart.pointerX) / rect.width) * (cropRect.width / this.imageMetrics.width);
      const deltaY = ((event.clientY - this.dragStart.pointerY) / rect.height) * (cropRect.height / this.imageMetrics.height);
      const nextX = this.clamp(this.dragStart.cropX - deltaX, 0, 1);
      const nextY = this.clamp(this.dragStart.cropY - deltaY, 0, 1);

      if (nextX !== this.crop.x || nextY !== this.crop.y) {
        this.crop.x = nextX;
        this.crop.y = nextY;
        this.cropMoved = true;
        this.updateCropPreview();
      }
    }

    handleCropPointerUp(event) {
      this.activePointers.delete(event.pointerId);

      if (this.activePointers.size === 1) {
        // Pinch dropped back to one finger — re-arm the drag from the finger still down so the
        // image does not jump.
        const [remaining] = Array.from(this.activePointers.values());
        if (remaining) this.beginCropDrag(remaining.x, remaining.y);
        return;
      }

      if (this.activePointers.size >= 2) {
        // Still pinching with fewer fingers — re-seed the baseline against the remaining pair so
        // the scale does not jump when one of several fingers lifts.
        this.beginCropPinch();
        return;
      }

      // Last finger up — end the gesture and detach the window listeners.
      window.removeEventListener("pointermove", this.onCropPointerMove);
      window.removeEventListener("pointerup", this.onCropPointerUp);
      window.removeEventListener("pointercancel", this.onCropPointerUp);

      const wasGesture = this.gestureMultiTouch || this.cropMoved;
      this.pinch = null;
      this.dragStart = null;

      if (wasGesture) {
        // A real manipulation (pan or any multi-touch) ends double-tap tracking, so a tap that
        // follows a pinch/pan can never be mis-read as a double-tap.
        this.lastTap = null;
      } else if (event.type === "pointerup") {
        this.handlePossibleDoubleTap(event);
      }

      // Gesture ended with an actual crop change — no server render anymore (render-on-confirm),
      // but a retryable error state may self-heal from it.
      if (this.cropMoved) {
        this.cropMoved = false;
        this.maybeScheduleErrorRecovery();
      }
    }

    handleCropLostCapture(event) {
      // Safety net: if the browser silently revokes pointer capture (OS gesture takeover, etc.)
      // the matching pointerup/cancel can be lost. Drop the stranded pointer so it cannot poison
      // the next gesture (which would otherwise read a phantom second finger and start a pinch).
      // On a normal pointerup this fires after handleCropPointerUp already removed the id, so the
      // guard makes it a no-op in the common case.
      if (!this.activePointers.has(event.pointerId)) return;
      this.activePointers.delete(event.pointerId);

      if (this.activePointers.size === 0) {
        window.removeEventListener("pointermove", this.onCropPointerMove);
        window.removeEventListener("pointerup", this.onCropPointerUp);
        window.removeEventListener("pointercancel", this.onCropPointerUp);
        this.pinch = null;
        this.dragStart = null;
        this.cropMoved = false;
        this.gestureMultiTouch = false;
      } else if (this.activePointers.size >= 2) {
        this.beginCropPinch();
      } else {
        const [remaining] = Array.from(this.activePointers.values());
        if (remaining) this.beginCropDrag(remaining.x, remaining.y);
      }
    }

    handlePossibleDoubleTap(event) {
      const now = event.timeStamp;
      const x = event.clientX;
      const y = event.clientY;
      const moved = this.pointerDownInfo ? Math.hypot(x - this.pointerDownInfo.x, y - this.pointerDownInfo.y) : Infinity;
      const duration = this.pointerDownInfo ? now - this.pointerDownInfo.time : Infinity;

      // A press that moved or lingered is not a tap — reset the double-tap tracker.
      if (moved > 10 || duration > 400) {
        this.lastTap = null;
        return;
      }

      if (this.lastTap && now - this.lastTap.time < 300 && Math.hypot(x - this.lastTap.x, y - this.lastTap.y) < 40) {
        // Double tap toggles between fit (1x) and 2x, around the current crop center.
        this.lastTap = null;
        this.crop.scale = this.crop.scale > 1.01 ? 1 : 2;
        this.updateCropPreview();
        this.maybeScheduleErrorRecovery();
        return;
      }

      this.lastTap = { time: now, x, y };
    }

    createLocalPreview(file, session) {
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(file);
      this.objectUrlIsLocal = true;
      this.previewDataUrl = "";
      this.imageMetrics = null;
      this.sourceDimensions = null;
      this.resetCrop();
      if (this.cropImage) {
        this.cropImage.onload = () => {
          if (!this.isCurrent(session)) return;
          this.imageMetrics = {
            width: this.cropImage.naturalWidth,
            height: this.cropImage.naturalHeight,
          };
          // This onload also fires for rotation bakes (capped working bitmap) — only the pristine
          // original counts as the print file. SVG is vector, its intrinsic px are meaningless.
          if (this.cropImage.src === this.objectUrl && !this.isSvgFile()) {
            this.sourceDimensions = { ...this.imageMetrics };
          }
          this.updateCropPreview();
          this.updateCropZones();
          this.applyPreview();
        };
        this.cropImage.onerror = () => {
          if (!this.isCurrent(session)) return;
          this.abortActiveSession();
          if (this.cropElement) this.cropElement.hidden = true;
          if (this.previewEmpty) this.previewEmpty.hidden = false;
          this.setState(STATE.ERROR);
          this.failUpload(this.dataset.errorText || "Das Bild konnte nicht geladen werden.");
        };
        // Local blob URL is same-origin — drop any crossOrigin left from a prior PDF/HEIC raster load.
        this.cropImage.crossOrigin = null;
        this.cropImage.src = this.objectUrl;
      }
      if (this.cropElement) this.cropElement.hidden = false;
      if (this.previewEmpty) this.previewEmpty.hidden = true;
      this.updateCropPreview();
      this.applyPreview();
      this.resetButton.hidden = false;
    }

    createPendingServerPreview() {
      this.revokeObjectUrl();
      this.previewDataUrl = "";
      this.imageMetrics = null;
      this.resetCrop();
      if (this.cropImage) this.cropImage.src = "";
      if (this.cropElement) this.cropElement.hidden = true;
      if (this.previewEmpty) this.previewEmpty.hidden = false;
      if (this.resetButton) this.resetButton.hidden = false;
    }

    // Load the backend's full crop-source raster (PDF/HEIC) into the crop frame so it can be cropped
    // exactly like a local image. crossOrigin="anonymous" + the backend's CORS headers keep the canvas
    // untainted, so getPreviewDataUrl()/setChipThumbnail() can still export the baked crop.
    loadServerCropSource(url, session) {
      return new Promise((resolve) => {
        if (!this.isCurrent(session)) {
          resolve();
          return;
        }

        this.revokeObjectUrl();
        this.objectUrl = url;
        this.objectUrlIsLocal = false;
        // Empty so getPreviewDataUrl() bakes the crop via canvas (like an image) instead of returning
        // the raw full-frame raster.
        this.previewDataUrl = "";
        this.imageMetrics = null;

        if (!this.cropImage) {
          this.applyPreview();
          resolve();
          return;
        }

        this.cropImage.onload = () => {
          if (!this.isCurrent(session)) {
            resolve();
            return;
          }
          this.imageMetrics = {
            width: this.cropImage.naturalWidth,
            height: this.cropImage.naturalHeight,
          };
          if (this.cropElement) this.cropElement.hidden = false;
          if (this.previewEmpty) this.previewEmpty.hidden = true;
          this.updateCropPreview();
          this.updateCropZones();
          this.applyPreview();
          resolve();
        };
        this.cropImage.onerror = () => {
          if (!this.isCurrent(session)) {
            resolve();
            return;
          }
          this.abortActiveSession();
          if (this.cropElement) this.cropElement.hidden = true;
          if (this.previewEmpty) this.previewEmpty.hidden = false;
          this.setState(STATE.ERROR);
          this.failUpload(this.dataset.errorText || "Das Motiv konnte nicht geladen werden.");
          resolve();
        };
        this.cropImage.crossOrigin = "anonymous";
        this.cropImage.src = url;
      });
    }

    applyPreview() {
      if (!this.objectUrl) return;

      // Ensure a single overlay on the currently resolved wrapper — prevents an orphan
      // overlay left on a previously selected (now off-screen) slide after gallery swipes.
      this.removePreview();

      const mockup = this.getMockupUrl();
      const wrapper = this.getSelectedMediaWrapper();
      if (!wrapper) return;

      wrapper.style.position = "relative";
      wrapper.classList.add("hypemat-uploader-preview-active");

      // Hide the zoom (PhotoSwipe) trigger while the overlay is shown — zoom would open the
      // stock product photo without the customer's motif.
      const productMedia = wrapper.closest("product-media");
      if (productMedia) productMedia.classList.add("hypemat-uploader-has-preview");

      // removePreview() above guarantees a clean slate, so always create a fresh overlay.
      const preview = document.createElement("div");
      preview.className = "hypemat-uploader-preview";
      preview.dataset.hypematMediaPreview = "true";
      preview.innerHTML = [
        '<div class="hypemat-uploader-preview__artwork-clip" data-hypemat-preview-artwork-clip>',
        '<img class="hypemat-uploader-preview__artwork" src="" alt="" data-hypemat-preview-artwork>',
        "</div>",
        '<div class="hypemat-uploader-preview__mockup" data-hypemat-preview-mockup></div>',
      ].join("");
      wrapper.appendChild(preview);

      this.positionArtworkClip(preview.querySelector("[data-hypemat-preview-artwork-clip]"), wrapper);

      // The artwork is a baked, pre-cropped image that fills the format-aspect clip via object-fit (proven,
      // reliable). The S11 CSS-positioning variant regressed the live overlay in-browser, so it is
      // parked; the resize-decouple jank fix and the chip blob URL are kept.
      const artworkElement = preview.querySelector("[data-hypemat-preview-artwork]");
      // Only ever show the baked crop. Until the image has decoded the artwork stays empty
      // (mockup only) instead of showing the raw file, which object-fit would map to the
      // clip with the wrong framing.
      const previewUrl = this.getPreviewDataUrl();
      if (previewUrl) artworkElement.src = previewUrl;

      const mockupElement = preview.querySelector("[data-hypemat-preview-mockup]");
      if (mockup) mockupElement.style.backgroundImage = `url("${mockup}")`;
      this.setMockupProperties();
    }

    updateCropPreview() {
      if (this.cropScaleInput) {
        this.cropScaleInput.value = String(this.crop.scale);
        this.cropScaleInput.setAttribute("aria-valuetext", Math.round(this.crop.scale * 100) + "%");
      }
      this.setCropProperties();

      // Reuse the computed crop rect for the crop-frame image; the gallery overlay re-bakes its own
      // cropped image (updateProductPreviewArtwork) so it reliably fills the format-aspect clip.
      const cropRect = this.imageMetrics ? this.computeCropRect() : null;
      this.updateMediaPreviewLayout();
      if (this.cropImage) this.applyCropStyles(this.cropImage, null, cropRect);
      this.updateProductPreviewArtwork();

      // Auto-clear the client-side "crop too small" message the moment the customer fixes it
      // (zooming out) — clear-only, never show, so screen readers get no per-tick spam.
      if (
        this.cropTooSmallError &&
        cropRect &&
        cropRect.width >= MIN_CROP_EDGE_PIXELS &&
        cropRect.height >= MIN_CROP_EDGE_PIXELS
      ) {
        this.clearError();
      }
    }

    applyCropStyles(element, frameSize = null, cropRect = null) {
      if (!this.imageMetrics) return;

      // The frame is either the crop frame (measured) or the gallery clip (size passed in by
      // positionArtworkClip, so we don't force a second reflow re-reading it).
      let frameWidth;
      let frameHeight;
      if (frameSize) {
        frameWidth = frameSize.width;
        frameHeight = frameSize.height;
      } else {
        const frame = element.closest("[data-hypemat-crop-frame], [data-hypemat-preview-artwork-clip]");
        const frameRect = frame?.getBoundingClientRect();
        if (!frameRect?.width || !frameRect?.height) return;
        frameWidth = frameRect.width;
        frameHeight = frameRect.height;
      }

      const rect = cropRect || this.computeCropRect();
      const renderScale = Math.max(frameWidth / rect.width, frameHeight / rect.height);

      element.style.width = `${this.imageMetrics.width * renderScale}px`;
      element.style.height = `${this.imageMetrics.height * renderScale}px`;
      element.style.left = `${-rect.left * renderScale}px`;
      element.style.top = `${-rect.top * renderScale}px`;
      // Neutralize the overlay artwork's CSS inset:0 (right/bottom:0) so the inline left/top stay
      // authoritative — otherwise an RTL document drops `left` and pins the image to the wrong edge.
      element.style.right = "auto";
      element.style.bottom = "auto";
      element.style.transform = "none";
      element.style.objectFit = "fill";
    }

    updateMediaPreviewLayout() {
      document.querySelectorAll("[data-hypemat-media-preview]").forEach((preview) => {
        const wrapper = preview.closest(".product__media-image-wrapper");
        const clip = preview.querySelector("[data-hypemat-preview-artwork-clip]");
        this.positionArtworkClip(clip, wrapper);
      });
    }

    positionArtworkClip(clip, wrapper) {
      if (!clip || !wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      if (!wrapperRect.width || !wrapperRect.height) return;

      // The mockup is drawn with background-size: contain, so the band is computed against
      // that same contained square (with a frame) or as the centred full-width band (without).
      const band = this.getCore().artworkBandRect(
        this.artworkFrame,
        this.cropAspect.value,
        wrapperRect.width,
        wrapperRect.height
      );

      clip.style.width = `${band.width}px`;
      clip.style.height = `${band.height}px`;
      clip.style.left = `${band.left}px`;
      clip.style.top = `${band.top}px`;
    }

    updateProductPreviewArtwork() {
      const previewUrl = this.getPreviewDataUrl();
      if (!previewUrl) return;

      document.querySelectorAll("[data-hypemat-preview-artwork]").forEach((element) => {
        element.src = previewUrl;
      });
    }

    getPreviewDataUrl() {
      // PDF/HEIC now hold a real cross-origin raster in cropImage, so they bake the crop via canvas
      // exactly like an image (the crossorigin load keeps the canvas untainted). Until the image has
      // decoded there is nothing to bake — return "" and let the crop image's load event re-bake
      // (previewBakePending). Never hand out the raw file: its aspect differs from the crop, so the
      // gallery would show the wrong framing (seen live on 2026-09-02 with a 9:4 file in a 46:21 crop).
      if (!this.imageMetrics || !this.cropImage?.complete || !this.cropImage.naturalWidth) {
        this.previewBakePending = true;
        return "";
      }
      this.previewBakePending = false;

      const cropRect = this.computeCropRect();
      const canvas = document.createElement("canvas");
      canvas.width = 1680;
      canvas.height = Math.round(1680 / this.cropAspect.value);

      const context = canvas.getContext("2d");
      if (!context) return "";

      context.drawImage(
        this.cropImage,
        cropRect.left,
        cropRect.top,
        cropRect.width,
        cropRect.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      this.previewDataUrl = canvas.toDataURL("image/jpeg", 0.9);
      return this.previewDataUrl;
    }

    setChipThumbnail() {
      // One-time cropped thumbnail for the collapsed chip, as a revocable Blob object URL rather
      // than a multi-MB base64 data URL that would linger in the PDP DOM.
      if (this.isPdfFile() || !this.imageMetrics || !this.cropImage?.complete || !this.chipThumb) return;

      const cropRect = this.computeCropRect();
      const canvas = document.createElement("canvas");
      canvas.width = 1680;
      canvas.height = Math.round(1680 / this.cropAspect.value);

      const context = canvas.getContext("2d");
      if (!context || !canvas.toBlob) return;

      context.drawImage(
        this.cropImage,
        cropRect.left,
        cropRect.top,
        cropRect.width,
        cropRect.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      // Guard on the upload (this.generation) AND a per-collapse token: a re-confirm reuses the
      // same upload generation, so without the token a stale earlier encode could land after the
      // newer one and repaint/revoke the wrong blob.
      this.thumbGeneration = (this.thumbGeneration || 0) + 1;
      const generation = this.generation;
      const thumbGeneration = this.thumbGeneration;
      canvas.toBlob(
        (blob) => {
          // Superseded or torn down — drop this blob (no object URL was created, nothing to free).
          if (
            !blob ||
            this.generation !== generation ||
            this.thumbGeneration !== thumbGeneration ||
            !this.chipThumb
          ) {
            return;
          }
          this.revokeCroppedBlobUrl();
          this.croppedBlobUrl = URL.createObjectURL(blob);
          this.chipThumb.src = this.croppedBlobUrl;
        },
        "image/jpeg",
        0.9
      );
    }

    revokeCroppedBlobUrl() {
      if (this.croppedBlobUrl) {
        URL.revokeObjectURL(this.croppedBlobUrl);
        this.croppedBlobUrl = null;
      }
    }

    // The crop box at full zoom-out (scale 1): the largest cut the current bitmap can ever yield
    // at the mat's aspect ratio. Shared with getImageTooSmallMessage, which uses it to tell a
    // too-deep zoom apart from a file that is simply too small to print at any zoom level.
    computeBaseCropSize() {
      const cropAspectRatio = this.cropAspect.value;
      const { width, height } = this.imageMetrics;

      return width / height > cropAspectRatio
        ? { width: height * cropAspectRatio, height }
        : { width, height: width / cropAspectRatio };
    }

    computeCropRect() {
      const { width: baseWidth, height: baseHeight } = this.computeBaseCropSize();

      const cropWidth = Math.max(1, Math.floor(baseWidth / this.crop.scale));
      const cropHeight = Math.max(1, Math.floor(baseHeight / this.crop.scale));
      const centerX = this.crop.x * this.imageMetrics.width;
      const centerY = this.crop.y * this.imageMetrics.height;
      const left = Math.round(Math.min(Math.max(centerX - cropWidth / 2, 0), this.imageMetrics.width - cropWidth));
      const top = Math.round(Math.min(Math.max(centerY - cropHeight / 2, 0), this.imageMetrics.height - cropHeight));

      return {
        left,
        top,
        width: Math.min(cropWidth, this.imageMetrics.width - left),
        height: Math.min(cropHeight, this.imageMetrics.height - top),
      };
    }

    // Render-on-confirm: crop interactions no longer trigger per-nudge server renders —
    // /complete runs once after the upload and at "Motiv übernehmen" (flushPendingComplete).
    // The ONE exception: while a retryable error is showing (SERVER_BUSY, network blip, the
    // server-side IMAGE_TOO_SMALL backstop), the next crop interaction re-attempts the commit,
    // so a customer who never spots the retry button still recovers by just moving the image.
    // After fatal errors scheduleCompleteUpload's own guards make this a no-op (uploadId gone).
    maybeScheduleErrorRecovery() {
      if (this.state !== STATE.ERROR) return;
      this.scheduleCompleteUpload();
    }

    // Debounced server commit. Not wired to crop interactions anymore (render-on-confirm) —
    // remaining callers: handleRetry, maybeScheduleErrorRecovery and the rotation-bake rollback.
    scheduleCompleteUpload() {
      this.setCropProperties();
      const session = this.session;
      if (!session || !session.uploadId || !session.uploadFinished) return;

      this.setState(STATE.PROCESSING);
      window.clearTimeout(this.completeTimeout);
      session.completeRequestId += 1;
      const completeRequestId = session.completeRequestId;
      this.completeTimeout = window.setTimeout(() => {
        this.completeUpload(session, completeRequestId).catch((error) => {
          if (!this.isCurrent(session) || error.name === "AbortError") return;
          console.error("Hypemat uploader: crop processing failed", error);
          this.setState(STATE.ERROR);
          this.failUpload(this.getErrorMessage(error), error);
        });
      }, 500);
    }

    resetCrop() {
      this.crop = {
        x: 0.5,
        y: 0.5,
        scale: 1,
        rotation: 0,
        aspectRatio: this.cropAspect.label,
      };
      this.setCropProperties();
    }

    parseArtworkFrame() {
      return this.getCore().parseArtworkFrame(this.dataset.artworkFrame);
    }

    // hypemat-uploader-core.js is loaded with defer before this file; the inline fallback only
    // covers the (unlikely) case that the core asset failed to load.
    getCore() {
      return (
        window.HypematUploaderCore || {
          classifyAttachCartResult(status) {
            if (status >= 200 && status < 300) return "success";
            return "retry";
          },
          attachRetryDelayMs(attempt) {
            return Math.min(500 * Math.pow(2, Math.max(0, attempt - 1)), 4000);
          },
          parseArtworkFrame() {
            return null;
          },
          artworkBandRect(frame, aspectRatio, wrapperWidth, wrapperHeight) {
            let width = wrapperWidth;
            let height = width / aspectRatio;
            if (height > wrapperHeight) {
              height = wrapperHeight;
              width = height * aspectRatio;
            }
            return { left: (wrapperWidth - width) / 2, top: (wrapperHeight - height) / 2, width, height };
          },
        }
      );
    }

    parseCropAspect() {
      const fallback = { label: "42:25", value: 42 / 25 };
      const raw = String(this.dataset.cropAspect || "").trim();
      const match = raw.match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/);
      if (!match) return fallback;

      const width = Number(match[1]);
      const height = Number(match[2]);
      if (!width || !height) return fallback;

      return { label: `${match[1]}:${match[2]}`, value: width / height };
    }

    setCropProperties() {
      this.setProperty("_UploadCropX", this.formatCropNumber(this.crop.x));
      this.setProperty("_UploadCropY", this.formatCropNumber(this.crop.y));
      this.setProperty("_UploadCropScale", this.formatCropNumber(this.crop.scale));
      this.setProperty("_UploadCropRotation", String(this.crop.rotation || 0));
      this.setProperty("_UploadCropAspectRatio", this.crop.aspectRatio);
    }

    formatCropNumber(value) {
      return Number(value).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    getSelectedMediaWrapper() {
      const productMedia = document.querySelector("product-media");
      if (!productMedia) return null;

      // Prefer the currently selected slide, then the initial selection. When the user swipes
      // onto a video/3D slide, Flickity moves is-selected there (no image wrapper) while
      // is-initial-selected stays on the original image slide, so the overlay parks there.
      const selectedImage =
        productMedia.querySelector(".product__media-item.is-selected .product__media-image-wrapper") ||
        productMedia.querySelector(".product__media-item.is-initial-selected .product__media-image-wrapper");
      if (selectedImage) return selectedImage;

      // Neither the selected nor the initial slide is an image (e.g. featured media is a
      // video/3D model) — fall back to the first non-filtered image slide.
      return productMedia.querySelector(".product__media-item:not(.is-filtered) .product__media-image-wrapper");
    }

    getMockupUrl() {
      return this.getMockupConfig().mockupUrl;
    }

    getMockupConfig() {
      const variantId = this.getCurrentVariantId();
      const uploadType = this.dataset.uploadType || "playmat_1p";
      const globalConfig = window.HypematMockups?.[uploadType] || {};
      const globalVariantConfig = this.normalizeMockupConfig(globalConfig.variants?.[variantId]);
      const blockVariantConfig = this.normalizeMockupConfig(this.mockupMap[variantId]);
      const globalFallbackConfig = this.normalizeMockupConfig(globalConfig.fallback);
      // The hard-coded default mockup is a 1-player playmat frame — it must never appear on
      // other formats. Those fall back to "no overlay" (empty URL): the gallery shows the bare
      // artwork band and the cart preview uses its existing plain-image path.
      const defaultConfig = {
        mockupUrl: this.dataset.defaultMockupOverlay || (uploadType === "playmat_1p" ? DEFAULT_PLAYMAT_MOCKUP_URL : ""),
        zoneLabel: "",
      };

      return globalVariantConfig || blockVariantConfig || globalFallbackConfig || defaultConfig;
    }

    normalizeMockupConfig(config) {
      if (!config) return null;

      if (typeof config === "string") {
        return {
          mockupUrl: config,
          zoneLabel: "",
        };
      }

      const mockupUrl = config.mockupUrl || config.mockupOverlayUrl || config.url || "";
      if (!mockupUrl) return null;

      return {
        mockupUrl,
        zoneLabel: config.zoneLabel || config.label || "",
      };
    }

    setMockupProperties(mockupConfig = this.getMockupConfig()) {
      const normalizedConfig = this.normalizeMockupConfig(mockupConfig);

      this.setProperty("_UploadMockupOverlayUrl", normalizedConfig?.mockupUrl || "");
      this.setProperty("_UploadZoneLabel", normalizedConfig?.zoneLabel || "");
    }

    removePreview() {
      document.querySelectorAll("[data-hypemat-media-preview]").forEach((preview) => preview.remove());
      document.querySelectorAll(".hypemat-uploader-preview-active").forEach((wrapper) => {
        wrapper.classList.remove("hypemat-uploader-preview-active");
      });
      document.querySelectorAll("product-media.hypemat-uploader-has-preview").forEach((media) => {
        media.classList.remove("hypemat-uploader-has-preview");
      });
    }

    revokeObjectUrl() {
      if (this.objectUrl && this.objectUrlIsLocal) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
      this.objectUrlIsLocal = false;
      // Every path that discards the base image must also discard what was derived from it.
      this.clearRotationArtifacts();
    }

    reset() {
      this.file = null;
      this.declaredType = "";
      this.clearActiveUpload();
      if (this.fileInput) this.fileInput.value = "";
      if (this.cropImage) this.cropImage.src = "";
      if (this.cropElement) this.cropElement.hidden = true;
      if (this.previewEmpty) this.previewEmpty.hidden = false;
      if (this.fileMeta) this.fileMeta.hidden = true;
      this.resetCrop();
      this.clearProperties();
      this.clearError();
      this.hideIntroHint();
      this.zonesVisible = true;
      this.updateCropZones();
      this.setStatus("");
      this.removePreview();
      this.revokeObjectUrl();
      if (this.resetButton) this.resetButton.hidden = true;
      // Clear the collapsed chip so no stale filename/thumbnail lingers, and free its blob URL.
      this.revokeCroppedBlobUrl();
      if (this.chipName) this.chipName.textContent = "";
      this.clearChipMeta();
      if (this.chipThumb) {
        this.chipThumb.src = "";
        this.chipThumb.hidden = true;
      }
      if (this.chipIcon) this.chipIcon.hidden = true;
      delete this.dataset.fileKind;
      this.dataset.dragging = "false";
      this.setState(STATE.IDLE);
    }

    clearActiveUpload() {
      this.abortActiveSession();
      this.session = null;
      this.imageMetrics = null;
      this.sourceDimensions = null;
      this.previewDataUrl = "";
      this.removePreview();
      this.revokeObjectUrl();
      this.revokeCroppedBlobUrl();
      this.clearProperties();
      this.setStatus("");
    }

    clearProperties() {
      this.querySelectorAll("[data-hypemat-property]").forEach((input) => {
        input.value = "";
        input.disabled = true;
      });

      this.setProperty("_UploadType", this.dataset.uploadType);
      this.setProperty("_UploadVariantId", this.getCurrentVariantId());
      this.setMockupProperties("");
      this.setCropProperties();
    }

    setState(state) {
      this.state = state;
      this.dataset.state = state;
      this.setAttribute("aria-busy", state === STATE.UPLOADING || state === STATE.PROCESSING ? "true" : "false");
      // Own the bottom bar (suppress the Focal product-sticky-form on mobile) only WHILE
      // configuring — our "Motiv übernehmen" sticky owns it then. Once applied we release it so the
      // theme's own (yellow) add-to-cart returns and is the sole buy button (Design B). PROCESSING
      // is included so a debounced re-crop never makes the bar flicker. Write only on change.
      const ownsBottomBar = state === STATE.PROCESSING || state === STATE.READY;
      const nextStickyActive = ownsBottomBar ? "true" : "false";
      if (this.dataset.stickyActive !== nextStickyActive) this.dataset.stickyActive = nextStickyActive;
      this.updateSubmitButtons();
      this.updateConfirmControl();
      this.updateStickyBar();
      this.syncSlowHint();
    }

    // Patience hint for long waits: big files render 30-60 s server-side (PDFs similar, and the
    // render queue can add more), so after 5 s of continuous waiting the status line explains the
    // delay instead of leaving the customer staring at a spinner. Time-based on purpose — it also
    // covers slow connections and queue waits, which no file-size heuristic would see. One timer
    // per waiting episode: it survives the UPLOADING -> PROCESSING hop and is cleared whenever the
    // wait ends (READY/ERROR/APPLIED with an idle confirm button).
    syncSlowHint() {
      const waiting = this.state === STATE.UPLOADING || this.state === STATE.PROCESSING || this.confirmBusy;

      if (!waiting) {
        if (this.slowHintTimer) {
          clearTimeout(this.slowHintTimer);
          this.slowHintTimer = null;
        }
        return;
      }

      if (this.slowHintTimer) return;

      this.slowHintTimer = setTimeout(() => {
        // Re-check: the wait may have ended between scheduling and firing.
        const stillWaiting = this.state === STATE.UPLOADING || this.state === STATE.PROCESSING || this.confirmBusy;
        if (stillWaiting && this.dataset.slowHint) this.setStatus(this.dataset.slowHint);
      }, SLOW_HINT_DELAY_MS);
    }

    updateSubmitButtons() {
      const busy = this.state === STATE.UPLOADING || this.state === STATE.PROCESSING;
      const locked = (this.required && this.state !== STATE.APPLIED) || busy;

      this.getSubmitButtons().forEach((button) => {
        // aria-disabled + dim class instead of the disabled attribute: a natively disabled
        // button swallows the click silently, so the customer never learns WHY it is dead.
        // This way the click reaches the submit gate (handleSubmit), which explains the
        // reason and moves focus to the uploader. Buttons the theme disabled itself (sold
        // out) are left alone — we never write button.disabled, so we cannot fight the
        // theme's own availability handling.
        if (button.disabled) {
          button.removeAttribute("aria-disabled");
          button.classList.remove("hypemat-atc-locked");
          return;
        }

        if (locked) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
        button.classList.toggle("hypemat-atc-locked", locked);
      });
    }

    getSubmitButtons() {
      const selector = [
        `button[type="submit"][form="${CSS.escape(this.dataset.formId)}"]`,
        `[data-product-add-to-cart-button][form="${CSS.escape(this.dataset.formId)}"]`,
      ].join(",");

      const buttons = new Set(Array.from(document.querySelectorAll(selector)));
      this.form?.querySelectorAll('button[type="submit"], [data-product-add-to-cart-button]').forEach((button) => {
        buttons.add(button);
      });

      return Array.from(buttons);
    }

    updateConfirmControl() {
      if (this.confirmButton) {
        // Don't fight the applying state — setConfirmLoading owns the disabled flag while loading.
        if (this.confirmButton.classList.contains("is-loading")) return;
        this.confirmButton.disabled = this.state !== STATE.READY;
      }
    }

    handleStickyAction() {
      // Design B: the sticky bar carries only the apply action ("Motiv übernehmen"). Buying happens
      // via the theme's own (yellow) add-to-cart once the motif is applied. PROCESSING keeps the
      // button disabled, so a click there can't reach here.
      if (this.state === STATE.READY) this.handleConfirm();
    }

    updateStickyBar() {
      if (!this.sticky || !this.stickyAction) return;

      // Design B: the sticky bar carries ONLY the configurator action ("Motiv übernehmen") while
      // configuring (ready, plus the transient re-crop processing so it never flickers). It is NOT
      // a buy button — once applied it hides and the theme's own (yellow) add-to-cart takes over,
      // so yellow stays reserved for the actual purchase and there is no duplicate add-to-cart.
      const visible = this.state === STATE.PROCESSING || this.state === STATE.READY;
      this.sticky.hidden = !visible;
      if (!visible) return;

      // While applying, mirror the confirm button's busy state (label + disabled) so a second tap
      // cannot re-enter the flow. While a debounced re-crop is processing (not applying), keep the
      // apply label but disable, like the inline confirm, so the bar stays put without going
      // dead-but-tappable.
      const busy = this.confirmBusy;
      this.stickyAction.textContent = busy
        ? this.dataset.applyingText || this.dataset.confirmText || "Motiv übernehmen"
        : this.dataset.confirmText || "Motiv übernehmen";
      this.stickyAction.classList.toggle("is-loading", busy);
      this.stickyAction.setAttribute("aria-busy", busy ? "true" : "false");
      this.stickyAction.disabled = busy || this.state !== STATE.READY;
    }

    setStatus(message) {
      if (this.statusElement) this.statusElement.textContent = message;
      if (this.hostStatus) this.hostStatus.textContent = message;
    }

    showError(message) {
      // A real error owns the intro line — the variant hint must never talk over it.
      this.hideIntroHint();
      if (this.errorElement) {
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      // Mirror into the intro block: in IDLE the inline UI (and its error line) is hidden,
      // so a blocked add-to-cart click needs a visible message next to the upload CTA.
      // Intro and inline are mutually exclusive, so the message never shows twice.
      if (this.introError) {
        this.introError.textContent = message;
        this.introError.hidden = false;
      }

      if (this.hostError) {
        this.hostError.textContent = message;
        this.hostError.hidden = false;
      }
    }

    clearError() {
      if (this.errorElement) {
        this.errorElement.textContent = "";
        this.errorElement.hidden = true;
      }

      if (this.introError) {
        this.introError.textContent = "";
        this.introError.hidden = true;
      }

      if (this.hostError) {
        this.hostError.textContent = "";
        this.hostError.hidden = true;
      }

      // The retry offer lives and dies with the error it belongs to (success, reset, file
      // change and confirm-start all clear errors).
      if (this.retryButton) this.retryButton.hidden = true;
      this.cropTooSmallError = false;
    }

    // Session-fatal errors are the ones a retry can never fix: the backend deleted the S3
    // object (FILE_TOO_LARGE, SIZE_MISMATCH), the upload row is gone (404/410), or the file
    // itself is deterministically unprintable (content/type errors). Everything else — network
    // blips, 5xx incl. SERVER_BUSY, rate limits, claim collisions (409), IMAGE_TOO_SMALL (a
    // crop-parameter problem, not a file problem) — keeps the finished upload retryable: the
    // original is still valid in S3 and the backend built /complete to be retried.
    isRetryableUploadError(error) {
      if (!error) return false;

      const fatalCodes = [
        "FILE_TOO_LARGE",
        "SIZE_MISMATCH",
        "MIME_MISMATCH",
        "NOT_PREPARABLE",
        "UNSUPPORTED_FORMAT",
        "PDF_MULTIPAGE",
        "PDF_ENCRYPTED",
        "PDF_UNREADABLE",
        "PDF_PROCESSING_FAILED",
        "HEIC_PROCESSING_FAILED",
        "HEIC_MULTI_IMAGE",
        "ANIMATED_IMAGE",
        "IMAGE_TOO_LARGE",
        "IMAGE_DIMENSIONS_UNREADABLE",
        "IMAGE_UNREADABLE",
      ];
      if (error.code && fatalCodes.includes(error.code)) return false;
      if (error.status === 404 || error.status === 410) return false;
      return true;
    }

    failUpload(message, error = null) {
      // One silent second chance for NETWORK-class /complete failures (an error without any HTTP
      // status — the server never answered): the error log showed the server usually finishes the
      // render fine while the customer's connection dies waiting on the long response (rows went
      // `ready` seconds after the failure beacon, July/August data), and customers re-upload from
      // scratch instead of clicking the retry link. A coded server answer (4xx/5xx) is NOT
      // eligible — the server spoke, and the existing paths handle it. Guards: PUT finished (the
      // original is safely in S3), crop UI usable (PDF/HEIC with a failed /prepare must keep the
      // manual retry path, which re-runs the prepare chain), one attempt per session.
      const networkClass = error && typeof error.status !== "number" && error.name !== "AbortError";
      if (
        networkClass &&
        this.session?.uploadFinished &&
        !this.session.completeAutoRetried &&
        (!this.needsServerRaster() || this.imageMetrics)
      ) {
        this.session.completeAutoRetried = true;
        // Own event name so /admin/errors counts how often the silent retry fires — a following
        // upload_failed row for the same upload means it did NOT heal.
        this.reportClientError("complete_auto_retry", error);
        this.scheduleCompleteUpload();
        return;
      }

      // Report FIRST: the fatal path below clears session.uploadId, and the beacon must still
      // carry it — found live 2026-07-16, when an iPhone PUT abort produced an /admin/errors row
      // without its upload link because the id was already nulled at report time.
      this.reportClientError("upload_failed", error);

      // After a finished PUT the uploadId is the customer's retry token — only a session-fatal
      // error may discard it (discarding on a network blip forces a full re-upload of up to
      // 80 MB). Callers without a server error object (client-side validation, the <img>
      // onerror paths that already aborted the session) stay on the fatal path.
      const retryable = Boolean(this.session?.uploadFinished) && this.isRetryableUploadError(error);

      if (!retryable && this.session) {
        this.session.uploadId = null;
        this.session.etag = null;
      }
      // The DOM property mirrors are cleared either way: on the retry path the next successful
      // /complete repopulates them via applyLineItemProperties, and until then nothing stale
      // can reach a cart. The retry token itself lives on session.uploadId, not in the DOM.
      this.clearUploadIdentityProperties();
      this.showError(message);
      if (this.retryButton) this.retryButton.hidden = !retryable;
    }

    // Fire-and-forget diagnostics beacon to the upload backend (/v1/client-errors) so failures
    // customers see — and silent degradations — show up in the backend's /admin/errors list.
    // Strictly best effort: one attempt, no retry, every problem swallowed; this must never
    // influence the upload flow. The payload is sent as a plain string on purpose:
    // sendBeacon(url, string) posts text/plain, which is CORS-simple (no preflight); the
    // backend parses it manually. No file names, no free-form exception text — the backend
    // whitelists token-shaped fields only.
    reportClientError(event, error = null, overrides = {}) {
      try {
        if (!this.endpointBaseUrl) return;

        const payload = JSON.stringify({
          event,
          code: error?.code || "",
          status: typeof error?.status === "number" ? error.status : undefined,
          state: this.state || "",
          // Phase markers so the error log shows WHERE the flow broke (the 17./18.07. rows all
          // read "state=error" and needed DB forensics to tell a PUT abort from a crop-source
          // load failure): put = did the S3 upload finish, raster = local image vs. server
          // raster path (PDF/HEIC), cropUi = did the crop source ever get decoded.
          put: this.session ? (this.session.uploadFinished ? "done" : "pending") : "",
          // Only meaningful while the PUT never finished: elapsed ms until it died.
          putMs:
            this.session && !this.session.uploadFinished && this.session.putStartedAt
              ? Date.now() - this.session.putStartedAt
              : undefined,
          raster: this.needsServerRaster() ? "server" : "local",
          cropUi: this.imageMetrics ? "ready" : "none",
          uploadId: overrides.uploadId || this.session?.uploadId || "",
          uploadType: this.dataset.uploadType || "",
          productHandle: this.dataset.productHandle || "",
          viewportW: window.innerWidth,
          locale: document.documentElement.lang || "",
        });
        const url = `${this.endpointBaseUrl}/v1/client-errors`;

        if (navigator.sendBeacon && navigator.sendBeacon(url, payload)) return;
        fetch(url, {
          method: "POST",
          body: payload,
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
        }).catch(() => {});
      } catch (_error) {
        // Diagnostics only — swallow everything.
      }
    }

    async handleRetry() {
      const session = this.session;
      if (!session || !session.uploadId || !session.uploadFinished) return;

      this.clearError();

      // PDF/HEIC whose /prepare failed transiently never got a crop source — crop interactions
      // are guard-blocked without imageMetrics, so this retry re-runs the full
      // prepare -> complete chain instead of a bare /complete against an empty crop UI.
      if (this.needsServerRaster() && !this.imageMetrics) {
        this.setState(STATE.PROCESSING);
        try {
          await this.prepareServerRaster(session);
          if (!this.isCurrent(session) || this.state === STATE.ERROR) return;
          await this.completeUpload(session);
        } catch (error) {
          if (!this.isCurrent(session) || error.name === "AbortError") return;
          console.error("Hypemat uploader: retry failed", error);
          this.setState(STATE.ERROR);
          this.failUpload(this.getErrorMessage(error), error);
        }
        return;
      }

      // Same debounce, guards and error handling as the error-recovery path
      // (maybeScheduleErrorRecovery) — one code path for all completes.
      this.scheduleCompleteUpload();
    }

    clearUploadIdentityProperties() {
      ["Upload", "_UploadId", "_UploadProductionUrl", "_UploadOriginalPdfUrl", "_UploadOriginalFilename"].forEach((key) => {
        this.setProperty(key, "");
      });
    }

    setFileMeta(file) {
      if (this.fileName) this.fileName.textContent = file.name || "";
      if (this.fileSize) this.fileSize.textContent = this.formatBytes(file.size);
      if (this.fileMeta) this.fileMeta.hidden = false;
    }

    formatBytes(bytes) {
      const value = Number(bytes || 0);
      if (!Number.isFinite(value) || value <= 0) return "";

      const units = ["B", "KB", "MB", "GB"];
      let unitIndex = 0;
      let size = value;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
      }

      return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    }

    // Canonicalize the file's type once per selection. HEIC/HEIF is normalized to a single
    // "image/heic" so it matches the backend's detection (declared === detected → no MIME_MISMATCH),
    // and an empty browser type (common for HEIC) falls back to the extension so validation, the
    // declared MIME, and the server-raster branch all agree.
    resolveUploadType(file) {
      const type = (file?.type || "").toLowerCase();
      if (type === "image/heic" || type === "image/heif") return "image/heic";
      // A generic binary type is as good as no type (some Windows/Android pickers report it for
      // HEIC) — fall through to the extension so the file is not rejected on the picker's guess.
      if (type && type !== "application/octet-stream") return type;

      const name = (file?.name || "").toLowerCase();
      if (name.endsWith(".heic") || name.endsWith(".heif")) return "image/heic";
      if (name.endsWith(".pdf")) return "application/pdf";
      return "";
    }

    isPdfFile() {
      return this.declaredType === "application/pdf";
    }

    isHeicFile() {
      return this.declaredType === "image/heic" || this.declaredType === "image/heif";
    }

    isSvgFile() {
      return this.declaredType === "image/svg+xml";
    }

    // PDF and HEIC cannot be decoded by the browser into a croppable <img> (Safari aside), so both
    // route through the backend: upload -> /prepare (server rasterizes/transcodes) -> a full raster is
    // loaded into the SAME crop UI as images. Everything downstream then treats them like an image.
    needsServerRaster() {
      return this.isPdfFile() || this.isHeicFile();
    }

    // IMAGE_TOO_SMALL has two causes with opposite fixes. Usually the customer zoomed past the
    // 100 px production minimum and "zoom out" solves it. But when even the fully zoomed-out crop
    // stays below the minimum, the FILE is too small — typically a stock preview thumbnail picked
    // instead of the original (a 64x38 px PNG re-uploaded six times is what prompted this) — and
    // telling that customer to zoom out is a dead end, because they are already at scale 1.
    // Only then switch to the upload-the-original message, with the measured pixel size whenever
    // the browser decoded the original itself (sourceDimensions is null for PDF/HEIC rasters and
    // SVG, so those get the same advice without numbers).
    getImageTooSmallMessage() {
      const zoomMessage =
        this.dataset.imageTooSmallMessage || this.dataset.errorText || "Upload fehlgeschlagen. Bitte erneut versuchen.";
      if (!this.imageMetrics) return zoomMessage;

      const base = this.computeBaseCropSize();
      if (Math.floor(base.width) >= MIN_CROP_EDGE_PIXELS && Math.floor(base.height) >= MIN_CROP_EDGE_PIXELS) {
        return zoomMessage;
      }

      const format = this.dataset.imageTooSmallSourceMessage || "";
      if (this.sourceDimensions && format) {
        return format
          .replace("[width]", String(this.sourceDimensions.width))
          .replace("[height]", String(this.sourceDimensions.height));
      }

      return this.dataset.imageTooSmallSourceUnknownMessage || zoomMessage;
    }

    getErrorMessage(error) {
      const messages = {
        PDF_MULTIPAGE: this.dataset.pdfMultipageMessage,
        PDF_ENCRYPTED: this.dataset.pdfEncryptedMessage,
        PDF_UNREADABLE: this.dataset.pdfProcessingMessage,
        PDF_PROCESSING_FAILED: this.dataset.pdfProcessingMessage,
        PDF_PROCESSING_TIMEOUT: this.dataset.pdfProcessingMessage,
        HEIC_PROCESSING_FAILED: this.dataset.heicProcessingMessage,
        // Single-image rule: multi-image HEIC containers (burst shots) and animated WebP/APNG
        // are rejected server-side — we can't know which frame the customer meant to print.
        HEIC_MULTI_IMAGE: this.dataset.heicMultiImageMessage,
        ANIMATED_IMAGE: this.dataset.animatedImageMessage,
        // All three mean "the bytes are not what the file claims to be" (e.g. a JPEG renamed to
        // .heic): the declared type passed validation but the server-side byte detection refused it.
        // Map them to the localized invalid-type message instead of the raw English server text.
        NOT_PREPARABLE: this.dataset.invalidTypeMessage,
        MIME_MISMATCH: this.dataset.invalidTypeMessage,
        UNSUPPORTED_FORMAT: this.dataset.invalidTypeMessage,
        // Crop window fell below the production minimum (100 px edge) — zooming out or a
        // higher-resolution image fixes it; the session stays retryable.
        IMAGE_TOO_SMALL: this.getImageTooSmallMessage(),
        // Image exceeds the server's 100-megapixel decode cap — fatal, only a smaller export helps.
        IMAGE_TOO_LARGE: this.dataset.imageTooLargeMessage,
        // Both mean "the file's image data cannot be read" (corrupt/truncated export) — same
        // customer action either way: re-export or pick a different file.
        IMAGE_UNREADABLE: this.dataset.imageUnreadableMessage,
        IMAGE_DIMENSIONS_UNREADABLE: this.dataset.imageUnreadableMessage,
        // Render queue full/timed out (backend concurrency cap) — transient, retry helps.
        SERVER_BUSY: this.dataset.serverBusyMessage || this.dataset.errorText,
        FILE_TOO_LARGE: this.dataset.maxSizeMessage,
      };
      const generic = this.dataset.errorText || "Upload fehlgeschlagen. Bitte erneut versuchen.";

      if (messages[error?.code]) return messages[error.code];
      // Never show error.message: it is English technical text ("Failed to fetch", "Storage upload
      // failed with 403", "Internal server error."). The beacon already carries code + status.
      // The backend drops `code` on 5xx responses, so SERVER_BUSY (503) arrives as a bare status.
      if (error?.status === 503 || error?.status === 429) return this.dataset.serverBusyMessage || generic;
      // No HTTP status at all: the request never got an answer (connection dropped, offline).
      if (error && typeof error.status !== "number") return this.dataset.networkMessage || generic;
      return generic;
    }
  }

  if (!window.customElements.get("hypemat-custom-uploader")) {
    window.customElements.define("hypemat-custom-uploader", HypematCustomUploader);
  }
})();
